import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import VoiceRecorder from "@/components/VoiceRecorder";
import SuggestInput from "@/components/SuggestInput";
import PhotoUploader from "@/components/PhotoUploader";
import { Trash2, Plus, Save, Loader2, AlertCircle, ClipboardCheck, Send, Clock, Check, X, Lock } from "lucide-react";
import { SendPreJobCheckModal } from "@/pages/PreJobCheck";
import SignaturePad from "@/components/SignaturePad";
import UserPicker, { useDirectory, findUserByName } from "@/components/UserPicker";

const DEPARTAMENTOS = ["ADMIN", "CLIMA", "CONS", "EST", "ETEL", "FONT/PCI", "FV", "PDS"];
const TIPOS = [
  { v: "AVERIA", l: "Avería" },
  { v: "SERVICIO_PUNTUAL", l: "Servicio Puntual" },
  { v: "OBRA", l: "Obra" },
  { v: "MANTENIMIENTO", l: "Mantenimiento" },
  { v: "VARIOS", l: "Varios" },
];

// Local YYYY-MM-DD (never UTC — Canary summer time drifts one day back).
const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const emptyForm = {
  departamento: "ETEL",
  tipo_servicio: "OBRA",
  nombre_servicio: "",
  fecha: todayISO(),
  vehiculo: "",
  operarios: [{ nombre: "", horas: 0 }],
  materiales: [{ descripcion: "", cantidad: "" }],
  sin_material: false,
  descripcion_tarea: "",
  pre_jobs_realizado: false,
  servicio_finalizado: false,
  observaciones: "",
};

export default function NewWorkLog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "sadmin";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [lastTranscript, setLastTranscript] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pjcSendOpen, setPjcSendOpen] = useState(false);
  const [pjcStatus, setPjcStatus] = useState(null);
  // Draft id — starts as the URL param and can be auto-generated when the user
  // clicks "Enviar PJC" before the parte has been persisted.
  const [draftId, setDraftId] = useState(id || null);
  const photosRef = useRef(null);

  // Non-managers cannot edit a saved parte. Kick them back to the list.
  useEffect(() => {
    if (id && user && !isManager) {
      toast.error("Los partes guardados no se pueden modificar");
      navigate("/logs", { replace: true });
    }
  }, [id, user, isManager, navigate]);

  // Validation — returns object with booleans for each invalid field
  const errors = useMemo(() => {
    const has = (s) => Boolean((s || "").toString().trim());
    const validOperarios = form.operarios.filter(
      (o) => has(o.nombre) && Number(o.horas) > 0
    );
    const validMateriales = form.materiales.filter((m) => has(m.descripcion));
    return {
      nombre_servicio: !has(form.nombre_servicio),
      fecha: !has(form.fecha),
      vehiculo: !has(form.vehiculo),
      descripcion_tarea: !has(form.descripcion_tarea),
      operarios: validOperarios.length === 0,
      operarios_horas: form.operarios.some(
        (o) => has(o.nombre) && !(Number(o.horas) > 0)
      ),
      materiales: form.sin_material ? false : validMateriales.length === 0,
    };
  }, [form]);

  const hasErrors = Object.values(errors).some(Boolean);
  // Only highlight fields after the user has tried to submit (cleaner UX)
  const showErr = (k) => attemptedSubmit && errors[k];

  const missingItems = useMemo(() => {
    const items = [];
    if (errors.nombre_servicio) items.push("Nombre de obra / servicio");
    if (errors.fecha) items.push("Fecha");
    if (errors.vehiculo) items.push("Vehículo");
    if (errors.operarios) items.push("Personal (al menos un operario con nombre)");
    if (errors.operarios_horas) items.push("Horas de algún operario (no pueden ser 0)");
    if (errors.materiales) items.push("Material empleado (al menos un material)");
    if (errors.descripcion_tarea) items.push("Tarea realizada");
    return items;
  }, [errors]);

  useEffect(() => {
    if (!id) {
      // If we were navigated here from an order with prefill data (from
      // /orders "Completar y crear parte"), apply that first before hitting
      // /work-logs/last-mine.
      const prefill = location.state?.prefill;
      if (prefill) {
        setForm((f) => ({
          ...f,
          ...(prefill.nombre_servicio ? { nombre_servicio: prefill.nombre_servicio } : {}),
          ...(prefill.descripcion_tarea ? { descripcion_tarea: prefill.descripcion_tarea } : {}),
          ...(prefill.fecha ? { fecha: prefill.fecha } : {}),
        }));
        window.history.replaceState({}, "");
        toast.info("Datos rellenados desde tu orden de trabajo", { duration: 3000 });
        return;
      }
      // New parte: pre-fill recurring fields from user's last submitted parte
      api.get("/work-logs/last-mine").then((r) => {
        const last = r.data;
        if (!last) return;
        setForm((f) => ({
          ...f,
          departamento: last.departamento || f.departamento,
          tipo_servicio: last.tipo_servicio || f.tipo_servicio,
          nombre_servicio: last.nombre_servicio || "",
          vehiculo: last.vehiculo || "",
          // keep last team but reset hours to 0 — operator must fill in actual hours each day
          operarios:
            last.operarios?.length
              ? last.operarios.map((o) => ({ nombre: o.nombre, horas: 0 }))
              : f.operarios,
          // fecha stays today (set by emptyForm)
          // materiales, descripcion_tarea, observaciones, pre_jobs stay empty for new day
        }));
        if (last.nombre_servicio) {
          toast.info("Hemos rellenado los datos repetitivos de tu último parte", {
            duration: 3500,
          });
        }
      }).catch(() => {});
      return;
    }
    api.get(`/work-logs/${id}`).then((r) => {
      const d = r.data;
      setForm({
        ...emptyForm,
        ...d,
        operarios: d.operarios?.length ? d.operarios : emptyForm.operarios,
        materiales: d.materiales?.length ? d.materiales : emptyForm.materiales,
      });
      setPjcStatus(d.pre_job_check_status || null);
    }).catch(() => toast.error("No se pudo cargar el parte"));
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Merge helpers — used so a second voice dictation APPENDS rather than overwrites
  const _norm = (s) => (s || "").trim().toLowerCase();

  const users = useDirectory();

  const _mergeOperarios = (existing, incoming) => {
    const isEmptyRow = (o) => !_norm(o?.nombre);
    const cleanExisting = (existing || []).filter((o) => !isEmptyRow(o));
    const byName = new Map(cleanExisting.map((o) => [_norm(o.nombre), { ...o }]));
    let added = 0;
    let updated = 0;
    for (const item of incoming || []) {
      if (!item?.nombre || !_norm(item.nombre)) continue;
      // Try to map to a real user from the directory
      const matched = findUserByName(users, item.nombre);
      const nombre = matched ? matched.name : item.nombre.trim();
      const user_id = matched ? matched.id : null;
      const key = _norm(nombre);
      if (byName.has(key)) {
        const cur = byName.get(key);
        if (item.horas && Number(item.horas) > 0 && Number(item.horas) !== Number(cur.horas)) {
          byName.set(key, { ...cur, horas: Number(item.horas), user_id: cur.user_id || user_id });
          updated += 1;
        } else if (!cur.user_id && user_id) {
          byName.set(key, { ...cur, user_id });
        }
      } else {
        byName.set(key, { nombre, horas: Number(item.horas) || 0, user_id });
        added += 1;
      }
    }
    const out = Array.from(byName.values());
    return { list: out.length ? out : [{ nombre: "", horas: 0, user_id: null }], added, updated };
  };

  const _mergeMateriales = (existing, incoming) => {
    const isEmptyRow = (m) => !_norm(m?.descripcion);
    const cleanExisting = (existing || []).filter((m) => !isEmptyRow(m));
    const seen = new Set(cleanExisting.map((m) => _norm(m.descripcion)));
    const out = [...cleanExisting];
    let added = 0;
    for (const item of incoming || []) {
      const desc = (item?.descripcion || "").trim();
      if (!desc) continue;
      const key = _norm(desc);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ descripcion: desc, cantidad: (item.cantidad || "").trim() });
      added += 1;
    }
    return { list: out.length ? out : [{ descripcion: "", cantidad: "" }], added };
  };

  const _mergeText = (existing, incoming) => {
    if (!incoming) return { value: existing, added: false };
    const e = (existing || "").trim();
    const i = incoming.trim();
    if (!i) return { value: existing, added: false };
    if (!e) return { value: i, added: true };
    if (e.toLowerCase().includes(i.toLowerCase())) return { value: existing, added: false };
    return { value: `${e}\n${i}`, added: true };
  };

  const handleParsed = (fields, transcript) => {
    setLastTranscript(transcript);
    setForm((f) => {
      const ops = _mergeOperarios(f.operarios, fields.operarios);
      const mats = _mergeMateriales(f.materiales, fields.materiales);
      const desc = _mergeText(f.descripcion_tarea, fields.descripcion_tarea);
      const obs = _mergeText(f.observaciones, fields.observaciones);

      // Scalars: only overwrite when AI returned a non-empty value
      const next = {
        ...f,
        departamento: fields.departamento || f.departamento,
        tipo_servicio: fields.tipo_servicio || f.tipo_servicio,
        nombre_servicio: fields.nombre_servicio?.trim() || f.nombre_servicio,
        fecha: fields.fecha || f.fecha,
        vehiculo: fields.vehiculo?.trim() || f.vehiculo,
        operarios: ops.list,
        materiales: mats.list,
        descripcion_tarea: desc.value,
        observaciones: obs.value,
      };

      // Build a concise summary toast
      const bits = [];
      if (ops.added) bits.push(`+${ops.added} operario${ops.added > 1 ? "s" : ""}`);
      if (ops.updated) bits.push(`${ops.updated} actualizad${ops.updated > 1 ? "os" : "o"}`);
      if (mats.added) bits.push(`+${mats.added} material${mats.added > 1 ? "es" : ""}`);
      if (desc.added) bits.push("tarea ampliada");
      if (obs.added) bits.push("observaciones ampliadas");
      if (bits.length) {
        toast.success(bits.join(" · "));
      }
      return next;
    });
  };

  const addOperario = () => update("operarios", [...form.operarios, { nombre: "", horas: 0, user_id: null }]);
  const rmOperario = (i) => update("operarios", form.operarios.filter((_, idx) => idx !== i));
  const setOperario = (i, k, v) => {
    const next = [...form.operarios];
    next[i] = { ...next[i], [k]: k === "horas" ? Number(v) || 0 : v };
    update("operarios", next);
  };
  const setOperarioFull = (i, v) => {
    const next = [...form.operarios];
    next[i] = { ...next[i], user_id: v.user_id, nombre: v.nombre };
    update("operarios", next);
  };

  // ─────── Team Pre-Job Check state ───────
  // ONE PJC per parte, with a signature slot per operario. The person filling
  // the parte creates the PJC once, then passes the device to each operario
  // for their signature. No push notifications are sent.
  const [teamPjc, setTeamPjc] = useState(null);
  const [pjcFillOpen, setPjcFillOpen] = useState(false);
  const [pjcSigningFor, setPjcSigningFor] = useState(null); // operario slot object
  const reloadPjcs = async (overrideId) => {
    const targetId = overrideId || draftId;
    if (!targetId) return;
    try {
      const { data } = await api.get(`/work-logs/${targetId}/pjcs`);
      // Prefer team-mode PJCs (new model). If none, teamPjc stays null.
      const team = (data || []).find((p) => p.mode === "team");
      setTeamPjc(team || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { reloadPjcs(); }, [draftId]);

  // Ensure a persistable id exists before opening the PJC fill modal. If we're
  // on /new we silently create a draft parte and swap the URL so the user can
  // refresh without losing state.
  const ensureDraft = async () => {
    if (draftId) return draftId;
    const payload = {
      ...form,
      operarios: form.operarios.filter((o) => (o.nombre || "").trim()),
      materiales: form.sin_material ? [] : form.materiales.filter((m) => (m.descripcion || "").trim()),
      status: "draft",
    };
    const res = await api.post("/work-logs", payload);
    const newId = res.data.id;
    setDraftId(newId);
    window.history.replaceState(null, "", `/edit/${newId}`);
    return newId;
  };

  const openPjcFill = async () => {
    const validOps = form.operarios.filter((o) => o.user_id);
    if (!validOps.length) {
      toast.error("Selecciona operarios válidos antes de rellenar el PJC.");
      return;
    }
    await ensureDraft();
    setPjcFillOpen(true);
  };

  const submitPjcFill = async (formData) => {
    const validOps = form.operarios.filter((o) => o.user_id);
    const targetId = await ensureDraft();
    try {
      const { data } = await api.post(`/work-logs/${targetId}/pjc/team`, {
        operario_ids: validOps.map((o) => o.user_id),
        obra: form.nombre_servicio,
        trabajo: form.tipo_servicio,
        data: formData,
      });
      setTeamPjc(data);
      setPjcFillOpen(false);
      toast.success("Pre-Job Check creado. Pásale el dispositivo a cada operario para firmar.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el PJC.");
    }
  };

  const submitSlotSignature = async (dataUrl) => {
    if (!pjcSigningFor || !teamPjc) return;
    try {
      const { data } = await api.post(`/pre-job-checks/${teamPjc.id}/sign-slot`, {
        user_id: pjcSigningFor.user_id,
        signature: dataUrl,
      });
      setTeamPjc(data);
      setPjcSigningFor(null);
      toast.success(`${pjcSigningFor.name} firmó el PJC`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la firma.");
    }
  };

  const addMaterial = () => update("materiales", [...form.materiales, { descripcion: "", cantidad: "" }]);
  const rmMaterial = (i) => update("materiales", form.materiales.filter((_, idx) => idx !== i));
  const setMaterial = (i, k, v) => {
    const next = [...form.materiales];
    next[i] = { ...next[i], [k]: v };
    update("materiales", next);
  };

  // Derived: PJC gating for the save button.
  // DEPRECATED (v1.8): the PJC is now a personal daily safety check filled
  // from Home (/pjc/mine). It no longer gates the parte. Legacy team-PJC
  // records remain readable but do not block save.
  const pendingSlots = [];
  const pjcBlocking = false;

  const onSubmit = async (e) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    if (hasErrors) {
      setValidationOpen(true);
      return;
    }
    if (pjcBlocking) {
      toast.error(
        `Faltan ${pendingSlots.length} firma${pendingSlots.length === 1 ? "" : "s"} del Pre-Job Check. No se puede guardar el parte.`
      );
      return;
    }
    // Non-managers see a confirmation dialog on brand-new partes reminding
    // them the parte cannot be modified once saved.
    if (!draftId && !isManager) {
      setConfirmOpen(true);
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const payload = {
        ...form,
        operarios: form.operarios.filter(
          (o) => o.nombre?.trim() && Number(o.horas) > 0
        ),
        materiales: form.sin_material ? [] : form.materiales.filter((m) => m.descripcion?.trim()),
        status: "completed",
      };
      let savedId = draftId;
      if (draftId) {
        await api.put(`/work-logs/${draftId}`, payload);
        toast.success("Parte guardado");
      } else {
        const res = await api.post("/work-logs", payload);
        savedId = res.data.id;
        toast.success("Parte guardado");
      }
      // Upload any photos that were queued locally
      const pending = photosRef.current?.getPending?.() || [];
      if (pending.length && savedId) {
        try {
          const fd = new FormData();
          pending.forEach((f) => fd.append("files", f));
          await api.post(`/work-logs/${savedId}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(`${pending.length} foto${pending.length > 1 ? "s" : ""} subida${pending.length > 1 ? "s" : ""}`);
          photosRef.current?.clearPending?.();
        } catch (err) {
          toast.error("No se pudieron subir todas las fotos");
        }
      }
      navigate("/logs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="new-log-form">
      <header>
        <div className="label-up text-[#FF4500]">{id && form.status && form.status !== "draft" ? "Editar" : "Nuevo"} parte</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1">
          {id && form.status && form.status !== "draft" ? "Editar parte" : "Crear parte de trabajo"}
        </h1>
        <p className="text-zinc-600 text-sm mt-1">Rellena los datos del día. Usa la voz para ir más rápido.</p>
      </header>

      {<VoiceRecorder onParsed={handleParsed} hasExistingData={Boolean(lastTranscript || form.nombre_servicio || form.operarios.some(o => o.nombre))} />}

      {lastTranscript && (
        <div className="brutal-card p-4 bg-[#FFF5EE]">
          <div className="label-up text-[#FF4500]">Transcripción</div>
          <p className="text-sm mt-1 text-zinc-700" data-testid="last-transcript">{lastTranscript}</p>
        </div>
      )}

      {/* Section 1: detalles */}
      <section className="brutal-card p-5 space-y-4">
        <h2 className="font-display font-black text-xl">1 · Detalles del servicio</h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-up block mb-2">Departamento</label>
            <select
              data-testid="field-departamento"
              className="brutal-input w-full"
              value={form.departamento}
              onChange={(e) => update("departamento", e.target.value)}
            >
              {DEPARTAMENTOS.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="label-up block mb-2">Tipo</label>
            <select
              data-testid="field-tipo"
              className="brutal-input w-full"
              value={form.tipo_servicio}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  tipo_servicio: v,
                  // Reset the "servicio finalizado" flag when switching to a
                  // tipo where it doesn't apply, so it never ships stale true.
                  servicio_finalizado:
                    v === "AVERIA" || v === "SERVICIO_PUNTUAL" ? f.servicio_finalizado : false,
                }));
              }}
            >
              {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label-up block mb-2">
            Nombre de obra / servicio <span className="text-red-500">*</span>
          </label>
          <SuggestInput
            testId="field-nombre-servicio"
            value={form.nombre_servicio}
            onChange={(v) => update("nombre_servicio", v)}
            endpoint="/suggestions/obras"
            placeholder="Ej: FV_OBRA_2026_LA MARINA RUBICON"
            invalid={showErr("nombre_servicio")}
          />
          {showErr("nombre_servicio") && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={12} /> Este campo es obligatorio
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-up block mb-2">
              Fecha <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="field-fecha"
              data-error={showErr("fecha") || undefined}
              type="date"
              className={`brutal-input w-full ${showErr("fecha") ? "border-red-500 ring-2 ring-red-200" : ""}`}
              value={form.fecha}
              onChange={(e) => update("fecha", e.target.value)}
            />
            {showErr("fecha") && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle size={12} /> Obligatorio
              </p>
            )}
          </div>
          <div>
            <label className="label-up block mb-2">
              Vehículo <span className="text-red-500">*</span>
            </label>
            <SuggestInput
              testId="field-vehiculo"
              value={form.vehiculo}
              onChange={(v) => update("vehiculo", v)}
              endpoint="/suggestions/vehiculos"
              placeholder="Ej: Nissan Nv200"
              invalid={showErr("vehiculo")}
            />
            {showErr("vehiculo") && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle size={12} /> Obligatorio
              </p>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 select-none cursor-pointer">
          <input
            type="checkbox"
            data-testid="field-prejobs"
            className="w-5 h-5 brutal-border accent-[#FF4500]"
            checked={form.pre_jobs_realizado}
            onChange={(e) => update("pre_jobs_realizado", e.target.checked)}
          />
          <span className="text-sm font-bold uppercase tracking-wider">Pre-jobs realizado</span>
        </label>

        {/* Servicio finalizado — only relevant for punctual services or breakdowns */}
        {(form.tipo_servicio === "AVERIA" || form.tipo_servicio === "SERVICIO_PUNTUAL") && (
          <label
            className="flex items-center gap-2 select-none cursor-pointer"
            data-testid="field-servicio-finalizado-wrapper"
          >
            <input
              type="checkbox"
              data-testid="field-servicio-finalizado"
              className="w-5 h-5 brutal-border accent-emerald-600"
              checked={!!form.servicio_finalizado}
              onChange={(e) => update("servicio_finalizado", e.target.checked)}
            />
            <span className="text-sm font-bold uppercase tracking-wider text-emerald-700">
              Servicio finalizado
            </span>
          </label>
        )}
      </section>

      {/* Section 2: personal */}
      <section
        data-error={attemptedSubmit && errors.operarios ? "true" : undefined}
        className={`brutal-card p-5 space-y-4 ${
          attemptedSubmit && errors.operarios ? "border-red-500 ring-2 ring-red-200" : ""
        }`}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl">
            2 · Personal <span className="text-red-500">*</span>
          </h2>
          <button type="button" onClick={addOperario} data-testid="add-operario"
            className="brutal-btn-ghost h-10 px-3 inline-flex items-center gap-2 text-xs">
            <Plus size={14} /> Añadir
          </button>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-[1fr_100px_44px] gap-2 px-1">
          <span className="label-up">Nombre</span>
          <span className="label-up text-center">Horas</span>
          <span />
        </div>

        <ul className="space-y-3">
          {form.operarios.map((o, i) => {
            const nameFilled = (o.nombre || "").trim().length > 0;
            const horasInvalid = attemptedSubmit && nameFilled && !(Number(o.horas) > 0);
            return (
              <li key={i} className="grid grid-cols-[1fr_100px_44px] gap-2 items-center">
                <UserPicker
                  testId={`operario-nombre-${i}`}
                  value={{ user_id: o.user_id, nombre: o.nombre }}
                  onChange={(v) => setOperarioFull(i, v)}
                  invalid={attemptedSubmit && !o.user_id && nameFilled}
                  placeholder="Escribe un usuario…"
                />
                <input
                  data-testid={`operario-horas-${i}`}
                  data-error={horasInvalid || undefined}
                  type="number" step="0.5" min="0"
                  className={`brutal-input w-full text-center ${horasInvalid ? "border-red-500 ring-2 ring-red-200" : ""}`}
                  value={o.horas}
                  onChange={(e) => setOperario(i, "horas", e.target.value)}
                  placeholder="Horas"
                />
                <button type="button" onClick={() => rmOperario(i)}
                  data-testid={`rm-operario-${i}`}
                  className="h-12 w-11 brutal-border bg-white hover:bg-[#EF4444] hover:text-white flex items-center justify-center rounded-xl">
                  <Trash2 size={16} />
                </button>
              </li>
            );
          })}
        </ul>
        {attemptedSubmit && errors.operarios && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <AlertCircle size={12} /> Debes añadir al menos un operario con nombre y horas
          </p>
        )}
        {attemptedSubmit && !errors.operarios && errors.operarios_horas && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <AlertCircle size={12} /> Falta indicar las horas en algún operario
          </p>
        )}

        {/* Team PJC card removed (v1.8). PJC is now a personal daily check
            filled from the Home page (/pjc/mine) — not tied to a work log.
            Legacy team PJC data (if exists on this parte) is hidden from UI
            but preserved in the DB for audit. */}
      </section>

      {/* Section 3: materiales */}
      <section
        data-error={attemptedSubmit && errors.materiales ? "true" : undefined}
        className={`brutal-card p-5 space-y-4 ${
          attemptedSubmit && errors.materiales ? "border-red-500 ring-2 ring-red-200" : ""
        }`}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl">
            3 · Material empleado {!form.sin_material && <span className="text-red-500">*</span>}
          </h2>
          <button
            type="button"
            onClick={addMaterial}
            data-testid="add-material"
            disabled={form.sin_material}
            className="brutal-btn-ghost h-10 px-3 inline-flex items-center gap-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={14} /> Añadir
          </button>
        </div>

        {/* Toggle "No usar material" */}
        <label
          className="flex items-center gap-2 select-none cursor-pointer -mt-1"
          data-testid="sin-material-wrapper"
        >
          <input
            type="checkbox"
            data-testid="field-sin-material"
            className="w-5 h-5 brutal-border accent-slate-700"
            checked={!!form.sin_material}
            onChange={(e) => update("sin_material", e.target.checked)}
          />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-700">
            No usar material en este parte
          </span>
        </label>

        {/* Column headers */}
        {!form.sin_material && (
          <>
            <div className="grid grid-cols-[100px_1fr_44px] gap-2 px-1">
              <span className="label-up">Cantidad</span>
              <span className="label-up">Descripción</span>
              <span />
            </div>

            <ul className="space-y-3">
              {form.materiales.map((m, i) => (
                <li key={i} className="grid grid-cols-[100px_1fr_44px] gap-2 items-center">
                  <input
                    data-testid={`material-cantidad-${i}`}
                    className="brutal-input w-full"
                    value={m.cantidad}
                    onChange={(e) => setMaterial(i, "cantidad", e.target.value)}
                    placeholder="Cant."
                  />
                  <SuggestInput
                    testId={`material-desc-${i}`}
                    value={m.descripcion}
                    onChange={(v) => setMaterial(i, "descripcion", v)}
                    endpoint="/suggestions/materiales"
                    placeholder="Descripción del material"
                  />
                  <button type="button" onClick={() => rmMaterial(i)}
                    data-testid={`rm-material-${i}`}
                    className="h-12 w-11 brutal-border bg-white hover:bg-[#EF4444] hover:text-white flex items-center justify-center rounded-xl">
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {form.sin_material && (
          <p className="text-xs text-slate-500 italic">
            No se usará material en este parte.
          </p>
        )}
        {attemptedSubmit && errors.materiales && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <AlertCircle size={12} /> Añade al menos un material con descripción
          </p>
        )}
      </section>

      {/* Section 4: descripción */}
      <section className="brutal-card p-5 space-y-4">
        <h2 className="font-display font-black text-xl">4 · Tarea y observaciones</h2>
        <div>
          <label className="label-up block mb-2">
            Tarea realizada <span className="text-red-500">*</span>
          </label>
          <textarea
            data-testid="field-descripcion"
            data-error={showErr("descripcion_tarea") || undefined}
            className={`brutal-input w-full h-28 py-2 ${showErr("descripcion_tarea") ? "border-red-500 ring-2 ring-red-200" : ""}`}
            value={form.descripcion_tarea}
            onChange={(e) => update("descripcion_tarea", e.target.value)}
            placeholder="Embridado y conexionado de seriado, colocar bandeja zona inversores..."
          />
          {showErr("descripcion_tarea") && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={12} /> Describe brevemente la tarea realizada
            </p>
          )}
        </div>
        <div>
          <label className="label-up block mb-2">Observaciones</label>
          <textarea
            data-testid="field-observaciones"
            className="brutal-input w-full h-20 py-2"
            value={form.observaciones}
            onChange={(e) => update("observaciones", e.target.value)}
            placeholder="Notas adicionales..."
          />
        </div>
      </section>

      {/* Section 5: fotos */}
      <PhotoUploader workLogId={draftId || null} pendingRef={photosRef} />

      <div className="sticky bottom-20 z-30 space-y-2">
        {attemptedSubmit && hasErrors && (
          <div className="rounded-xl bg-red-50 border-2 border-red-300 px-4 py-2.5 text-sm text-red-700 flex items-center gap-2 shadow-md">
            <AlertCircle size={16} /> Faltan {missingItems.length} campos obligatorios — revisa los marcados en rojo
          </div>
        )}

        {pjcBlocking && (
          <div className="rounded-xl bg-amber-50 border-2 border-amber-300 px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2 shadow-md" data-testid="pjc-blocking-banner">
            <Clock size={16} /> Faltan {pendingSlots.length} firma{pendingSlots.length === 1 ? "" : "s"} del Pre-Job Check. El parte no puede guardarse.
          </div>
        )}

        <button
          type="submit"
          disabled={saving || pjcBlocking}
          data-testid="submit-log"
          className="brutal-btn-primary w-full inline-flex items-center justify-center gap-2 text-base disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          {id && form.status && form.status !== "draft" ? "Actualizar parte" : "Guardar parte"}
        </button>
      </div>

      <SendPreJobCheckModal
        open={pjcSendOpen}
        onOpenChange={setPjcSendOpen}
        workLogId={draftId || null}
        obra={form.obra || form.nombre_servicio}
        trabajo={form.tipo_servicio}
        onSent={() => setPjcStatus("pending")}
      />

      {pjcFillOpen && (
        <TeamPjcFillModal
          obra={form.nombre_servicio}
          trabajo={form.tipo_servicio}
          fecha={form.fecha}
          onCancel={() => setPjcFillOpen(false)}
          onSubmit={submitPjcFill}
        />
      )}
      {pjcSigningFor && (
        <SlotSignatureModal
          slot={pjcSigningFor}
          onCancel={() => setPjcSigningFor(null)}
          onSign={submitSlotSignature}
        />
      )}

      {/* Save confirmation modal — non-managers only */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmOpen(false)}
          data-testid="save-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock size={22} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-display font-extrabold text-xl">¿Guardar parte?</h3>
                <p className="text-xs text-slate-500">Esta acción es definitiva</p>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              Este parte <b>no podrá ser modificado</b> una vez guardado.
              Por favor revisa que todo esté correcto antes de continuar.
              <br />
              <span className="text-slate-500 text-xs">
                Si necesitas corregir algo después, tendrás que pedirlo a un administrador.
              </span>
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                data-testid="save-confirm-cancel"
                className="flex-1 h-11 rounded-xl border border-slate-200 text-sm font-bold uppercase tracking-wider hover:bg-slate-50"
              >
                Revisar
              </button>
              <button
                type="button"
                onClick={doSave}
                disabled={saving}
                data-testid="save-confirm-ok"
                className="flex-1 h-11 rounded-xl bg-[#0B2A4A] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#0a2340] disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Sí, guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation modal */}
      {validationOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setValidationOpen(false)}
          data-testid="validation-modal"
        >
          <div
            className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle size={22} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-display font-extrabold text-xl">Faltan campos</h3>
                <p className="text-xs text-slate-500">
                  Antes de guardar, rellena lo siguiente:
                </p>
              </div>
            </div>
            <ul className="mt-4 space-y-2">
              {missingItems.map((m) => (
                <li
                  key={m}
                  className="flex items-start gap-2 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-800"
                >
                  <AlertCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                setValidationOpen(false);
                // scroll to first error
                setTimeout(() => {
                  const firstErr = document.querySelector("[data-error='true']");
                  firstErr?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 50);
              }}
              data-testid="validation-modal-close"
              className="brutal-btn-primary w-full mt-5"
            >
              Entendido, lo corrijo
            </button>
          </div>
        </div>
      )}
    </form>
  );
}


// ─────────── Team PJC section (single PJC + N signature slots) ───────────
function TeamPjcSection({ pjc, operarios, onOpenFill, onSignSlot }) {
  const hasValidOps = operarios.length > 0;
  if (!pjc) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 p-4 text-center space-y-2">
        <div className="label-up text-slate-500">Pre-Job Check del equipo</div>
        <p className="text-xs text-slate-500 max-w-xs mx-auto">
          {hasValidOps
            ? "Rellena una única lista de verificación de seguridad y recoge la firma de cada operario en este dispositivo."
            : "Añade operarios válidos para poder rellenar el Pre-Job Check."}
        </p>
        <button
          type="button"
          onClick={onOpenFill}
          disabled={!hasValidOps}
          data-testid="pjc-team-open"
          className="mx-auto brutal-btn-primary h-11 px-4 inline-flex items-center gap-2 text-sm disabled:opacity-50"
        >
          <ClipboardCheck size={16} /> Rellenar Pre-Job Check
        </button>
      </div>
    );
  }

  const slots = pjc.operarios || [];
  const signed = slots.filter((s) => s.status === "signed").length;
  const allSigned = signed === slots.length && slots.length > 0;

  return (
    <div className="rounded-xl border-2 border-slate-200 bg-white p-4 space-y-3" data-testid="pjc-team-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-up text-[#0B2A4A]">Pre-Job Check del equipo</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {signed}/{slots.length} firmas · Creado por {pjc.sender_name || "—"}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
            allSigned ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800 animate-pulse"
          }`}
          data-testid="pjc-team-status"
        >
          {allSigned ? <Check size={12} /> : <Clock size={12} />}
          {allSigned ? "Firmado" : "Pendiente firmas"}
        </span>
      </div>
      <ul className="space-y-2">
        {slots.map((s) => (
          <li
            key={s.user_id}
            className="flex items-center gap-2 rounded-lg border border-slate-200 p-2"
            data-testid={`pjc-slot-${s.user_id}`}
          >
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
              s.status === "signed" ? "bg-emerald-600" : "bg-slate-400"
            }`}>
              {(s.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{s.name}</div>
              <div className="text-[11px] text-slate-500">
                {s.status === "signed" ? "Firmado" : "Pendiente"}
              </div>
            </div>
            {s.status === "signed" ? (
              <img
                src={s.signature_url}
                alt="firma"
                className="h-9 w-20 object-contain bg-slate-50 border border-slate-200 rounded"
              />
            ) : (
              <button
                type="button"
                onClick={() => onSignSlot(s)}
                data-testid={`pjc-slot-sign-${s.user_id}`}
                className="h-9 rounded-lg px-3 bg-[#0E78C2] text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:opacity-90"
              >
                <Send size={14} /> Firmar
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}


// ─────────── Full-screen modal for filling the team PJC checklist ───────────
// Simplified per client request (Feb 2026):
// - Obra + Fecha are pulled from the parte (read-only)
// - Sections: Evaluación seguridad · EPIs · Formación · Plan de ejecución
// - Any NO answer blocks saving and shows the danger message.
const PJC_QUESTIONS = [
  {
    section: "Evaluación de seguridad",
    id: "seguridad",
    items: [
      ["entorno_seguro", "¿El entorno es aparentemente seguro para ejecutar los trabajos?"],
      ["capacidad_control", "¿Tenéis capacidad para controlar los riesgos identificados?"],
    ],
  },
  {
    section: "Equipos de protección individual (EPIs)",
    id: "epis",
    items: [
      ["epis_disponibles", "¿Disponéis de todos los EPIs necesarios según normativa y protocolo?"],
      ["epis_estado", "¿Están en buen estado, sin caducar y todos los compañeros disponen de ellos?"],
    ],
  },
  {
    section: "Formación técnica y PRL",
    id: "formacion",
    items: [
      ["formacion_prl", "¿Estáis vosotros y vuestros compañeros formados en PRL para la labor a realizar?"],
    ],
  },
  {
    section: "Plan de ejecución",
    id: "plan",
    // mode:"any" ⇒ basta con marcar SÍ en una de las opciones para pasar
    // (las demás pueden quedar en NO o sin marcar sin bloquear).
    mode: "any",
    hint: "Marca SÍ en la opción que corresponda. Basta con una.",
    items: [
      ["plan_especifico", "De acuerdo con el Plan Específico o Plan de Seguridad"],
      ["plan_prevencion", "De acuerdo con el Plan Específico de Prevención"],
      ["plan_general", "De acuerdo con el Plan General de Prevención de la empresa y con la normativa vigente según la formación realizada"],
    ],
  },
];

// Section-level validator. Returns { ok, hasBlockingNo }.
// - "all" sections (default): every item must be "si".
// - "any" sections: at least ONE item must be "si"; NO answers do not block.
function evaluatePjcSection(sec, answers) {
  const vals = sec.items.map(([k]) => answers[k]);
  const hasSi = vals.some((v) => v === "si");
  const hasNo = vals.some((v) => v === "no");
  const allAnswered = vals.every((v) => v === "si" || v === "no");
  if (sec.mode === "any") {
    return { ok: hasSi, hasBlockingNo: !hasSi && hasNo };
  }
  return { ok: allAnswered && !hasNo, hasBlockingNo: hasNo };
}

function TeamPjcFillModal({ obra, trabajo, fecha, onCancel, onSubmit }) {
  const [answers, setAnswers] = useState({}); // { key: "si" | "no" }
  const [observaciones, setObservaciones] = useState("");
  const [saving, setSaving] = useState(false);

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const sectionResults = PJC_QUESTIONS.map((sec) => ({
    sec,
    ...evaluatePjcSection(sec, answers),
  }));
  const canSave = sectionResults.every((r) => r.ok);
  const anyBlockingNo = sectionResults.some((r) => r.hasBlockingNo);
  const unansweredCount = sectionResults.filter((r) => !r.ok && !r.hasBlockingNo).length;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSubmit({
        instalacion: obra || "",
        trabajo: trabajo || "",
        fecha: fecha || todayISO(),
        hora: new Date().toTimeString().slice(0, 5),
        answers,
        observaciones,
      });
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="pjc-team-fill-modal"
      onClick={onCancel}
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <h3 className="font-display font-black text-lg text-[#0B2A4A]">Pre-Job Check</h3>
          <button type="button" onClick={onCancel} className="text-slate-500 hover:text-rose-600">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {/* Auto-filled context from parte */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
            <div><span className="font-semibold text-slate-700">Obra:</span> <span className="text-slate-600">{obra || "—"}</span></div>
            <div><span className="font-semibold text-slate-700">Fecha:</span> <span className="text-slate-600">{fecha || "—"}</span></div>
          </div>

          {/* Yes/No questionnaire */}
          {sectionResults.map(({ sec, ok: sectionOk }) => (
            <fieldset key={sec.id} data-testid={`pjc-section-${sec.id}`}>
              <legend className="label-up mb-2">{sec.section}</legend>
              {sec.hint && (
                <p className="text-[11px] text-slate-500 mb-2 -mt-1">{sec.hint}</p>
              )}
              <div className="space-y-2">
                {sec.items.map(([key, label]) => {
                  const value = answers[key];
                  // In "any" sections, a NO doesn't paint red once the section
                  // is already OK (i.e. at least one SÍ is marked). It only
                  // paints red when the section still isn't OK.
                  const isNoBlocking = value === "no" && !(sec.mode === "any" && sectionOk);
                  return (
                    <div
                      key={key}
                      className={`rounded-lg border p-3 ${
                        isNoBlocking ? "bg-rose-50 border-rose-300" : "bg-white border-slate-200"
                      }`}
                    >
                      <p className="text-xs text-slate-700 mb-2">{label}</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setAnswer(key, "si")}
                          data-testid={`pjc-q-${key}-si`}
                          className={`flex-1 h-10 rounded-lg text-xs font-bold transition-colors ${
                            value === "si"
                              ? "bg-emerald-600 text-white shadow-md"
                              : "bg-slate-100 text-slate-500 hover:bg-emerald-100"
                          }`}
                        >
                          SÍ
                        </button>
                        <button
                          type="button"
                          onClick={() => setAnswer(key, "no")}
                          data-testid={`pjc-q-${key}-no`}
                          className={`flex-1 h-10 rounded-lg text-xs font-bold transition-colors ${
                            value === "no"
                              ? "bg-rose-600 text-white shadow-md"
                              : "bg-slate-100 text-slate-500 hover:bg-rose-100"
                          }`}
                        >
                          NO
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}

          {/* Observations (optional) */}
          <label className="block">
            <span className="label-up block mb-1">Observaciones (opcional)</span>
            <textarea
              className="brutal-input w-full min-h-[70px]"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Notas, precauciones, incidencias, etc."
              data-testid="pjc-observaciones"
            />
          </label>

          {/* Blocking danger banner */}
          {anyBlockingNo && (
            <div
              className="rounded-xl bg-rose-600 text-white p-4 shadow-lg border-2 border-rose-800"
              data-testid="pjc-danger-banner"
            >
              <div className="font-black text-sm uppercase tracking-wide flex items-start gap-2">
                <AlertCircle size={20} className="shrink-0 mt-0.5" />
                <span>
                  DEBES DETENER LAS LABORES Y PONERTE EN CONTACTO CON UN SUPERIOR HASTA QUE LOS
                  TRABAJOS O EL PROTOCOLO SEAN SEGUROS O DISPONGAS DE LOS MEDIOS
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving || !canSave}
              data-testid="pjc-team-fill-submit"
              title={
                !canSave
                  ? anyBlockingNo
                    ? "No se puede firmar mientras haya respuestas NO bloqueantes"
                    : `Faltan ${unansweredCount} sección${unansweredCount === 1 ? "" : "es"} por responder`
                  : ""
              }
              className="flex-1 h-11 rounded-lg bg-[#FF4500] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Guardar Pre-Job Check
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────── Signature pad modal for one operario slot ───────────
function SlotSignatureModal({ slot, onCancel, onSign }) {
  const sigRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const url = sigRef.current?.getDataURL?.();
    if (!url) { toast.error("Dibuja una firma."); return; }
    setSaving(true);
    try { await onSign(url); } finally { setSaving(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="pjc-slot-modal"
      onClick={onCancel}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="label-up text-[#FF4500]">Firma del operario</div>
          <h3 className="font-display font-black text-xl">{slot.name}</h3>
          <p className="text-xs text-slate-500 mt-1">
            Dibuja tu firma con el dedo en el recuadro. Si te equivocas, pulsa &quot;Borrar&quot; y vuelve a firmar.
          </p>
        </div>
        <SignaturePad ref={sigRef} />
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="pjc-slot-save"
            className="flex-1 h-11 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Guardar firma
          </button>
        </div>
      </div>
    </div>
  );
}
